import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getSeries } from '../api';
import { coverageNote, day, geoName, num, stateLabel, stateNote, words } from '../format';
import DetailSkeleton from './DetailSkeleton';
import Flag from './Flag';
import SeriesChart from './SeriesChart';

export default function SeriesView() {
  const seriesId = Number(useParams().seriesId);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    getSeries(seriesId).then(setData).catch(() => setError('Could not load this term'));
  }, [seriesId]);

  if (error) return <div className="tw-empty">{error}</div>;
  if (!data) return <DetailSkeleton />;

  const { series: s, points, episodes } = data;
  const note = coverageNote(s);
  const named = Array.isArray(s.named_geos) ? s.named_geos : [];

  return (
    <div className="tw-detail">
      {/* Back to the company rather than to a top-level list: a term is only
          ever reached through one, and the company page is where the rest of
          its siblings are. */}
      <Link className="tw-back" to={`/companies/${s.company_id}`}>
        &larr; {s.company}
      </Link>

      <div className="tw-detail-head">
        <h1 className="tw-h1">
          <span className="sym">{s.ticker}</span>
          {s.term}
        </h1>
        <div className="tw-crumbs">
          <Link className="tw-link" to={`/companies/${s.company_id}`}>{s.company}</Link>
          <span className="sep">/</span>
          <span className="tw-crumb-geo">
            <Flag geo={s.geo} />
            {geoName(s.geo)}
          </span>
          {s.brand && (
            <>
              <span className="sep">/</span>
              {s.brand}
            </>
          )}
        </div>
        <div className="tw-tags">
          {s.term_role && <span className="tw-tag">{words(s.term_role)}</span>}
          {s.monitor_tier === 1 ? (
            <span className="tw-tag good">scanned weekly</span>
          ) : (
            <span className="tw-tag">context only</span>
          )}
          {s.search_relatedness === 'low' && <span className="tw-tag warn">ambiguous</span>}
        </div>
      </div>

      <div className="tw-block">
        <div className="tw-kv">
          <div>
            <div className="v">{num(s.latest_value)}</div>
            <div className="k">Latest index</div>
          </div>
          <div>
            <div className="v">
              {s.real_weeks ?? '—'}
              <small> / {s.weeks_returned ?? '—'}</small>
            </div>
            <div className="k">Weeks Google reported</div>
          </div>
          <div>
            <div className="v">
              {s.revenue_share_pct !== null && s.revenue_share_pct !== undefined
                ? `${num(s.revenue_share_pct)}%`
                : '—'}
            </div>
            <div className="k">Share of revenue</div>
          </div>
          <div>
            <div className="v" style={{ fontSize: 16 }}>{s.demand_proxy_quality ?? '—'}</div>
            <div className="k">Demand proxy quality</div>
          </div>
          <div>
            <div className="v">{episodes.length}</div>
            <div className="k">Trends on record</div>
          </div>
        </div>
        {note && <div className="tw-note">{note}</div>}
      </div>

      <div className="tw-block">
        <h2 className="tw-block-title">Five year search interest</h2>
        {points.length > 0 ? (
          <SeriesChart
            points={points}
            markers={episodes.map((e) => ({ time: e.started_on, text: 'break' }))}
          />
        ) : (
          <div className="tw-note">
            No series stored yet. This term is in the watchlist but has not been
            through a weekly run since the store was rebuilt.
          </div>
        )}
      </div>

      {episodes.length > 0 && (
        <div className="tw-block">
          <h2 className="tw-block-title">Trends on this term</h2>
          <div className="tw-table-wrap">
            <table className="tw-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>State</th>
                  <th className="n">Weeks</th>
                  <th className="n">Peak</th>
                  <th>Ended</th>
                </tr>
              </thead>
              <tbody>
                {episodes.map((e) => (
                  <tr key={e.episode_id} className="clickable"
                    onClick={() => navigate(`/trends/${e.episode_id}`)}>
                    <td className="lead">{day(e.started_on)}</td>
                    <td title={stateNote(e.state) || undefined}>{stateLabel(e.state)}</td>
                    <td className="n">{e.weeks_open ?? '—'}</td>
                    <td className="n">{num(e.peak_value)}</td>
                    <td>
                      {e.is_open
                        ? 'Open'
                        : `${words(e.close_reason) || 'closed'} ${day(e.closed_on)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(s.also_means || named.length > 0 || s.geo_pinned) && (
        <div className="tw-block">
          <h2 className="tw-block-title">Why this term, and why this market</h2>
          {s.also_means && <div className="tw-prose">{s.also_means}</div>}
          {/* A pinned market was not ranked, so the usual sentence about
              volume deciding it would credit a measurement that never ran. */}
          {s.geo_pinned ? (
            <div className="tw-note">
              We poll {s.geo} because it was set by hand, not ranked.
              {named.length > 0 && ` The extraction had named ${named.join(', ')}.`}
            </div>
          ) : named.length > 0 && (
            <div className="tw-note">
              The extraction named {named.join(', ')}. We poll {s.geo}, chosen by
              measured Google Ads volume across those candidates.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
